// =============================================================
// Formatting helpers
// =============================================================
import { formatDistanceToNowStrict, format } from "date-fns";

type DateInput = string | number | Date | null | undefined;

/**
 * Normalise a date value so JS treats it as UTC.
 *
 * The PHP backend stores datetimes as UTC strings **without** a timezone
 * indicator (e.g. "2026-06-08 19:05:42.000"). When `new Date()` receives
 * such a string it interprets it as *local* time, which shifts the
 * result by the user's UTC offset (−4 h for EDT → "4h ago" on a brand-
 * new ticket). Appending "Z" tells the parser the value is UTC.
 */
function toUtcDate(d: NonNullable<DateInput>): Date {
  if (d instanceof Date) return d;
  if (typeof d === "number") return new Date(d);
  // If the string already has a timezone indicator, leave it alone.
  if (/[Zz]$/.test(d) || /[+-]\d{2}:\d{2}$/.test(d)) return new Date(d);
  // Otherwise append 'Z' so the parser treats it as UTC.
  return new Date(d + "Z");
}

export const fmtRelative = (d: DateInput) =>
  d ? formatDistanceToNowStrict(toUtcDate(d), { addSuffix: true }) : "—";
export const fmtDateTime = (d: DateInput) =>
  d ? format(toUtcDate(d), "MMM d, yyyy · HH:mm") : "—";
export const fmtDate = (d: DateInput) =>
  d ? format(toUtcDate(d), "MMM d, yyyy") : "—";

export interface Countdown {
  label: string;
  ms: number;
  breached: boolean;
}

export function fmtCountdown(deadline: DateInput): Countdown {
  if (!deadline) return { label: "—", ms: 0, breached: false };
  const ms = toUtcDate(deadline).getTime() - Date.now();
  const breached = ms < 0;
  const abs = Math.abs(ms);
  const days = Math.floor(abs / 86_400_000);
  const hours = Math.floor((abs % 86_400_000) / 3_600_000);
  const minutes = Math.floor((abs % 3_600_000) / 60_000);
  const seconds = Math.floor((abs % 60_000) / 1_000);

  let label: string;
  if (days > 0) label = `${days}d ${hours}h`;
  else if (hours > 0) label = `${hours}h ${minutes}m`;
  else if (minutes > 0) label = `${minutes}m ${seconds}s`;
  else label = `${seconds}s`;

  return { label: breached ? `+${label}` : label, ms, breached };
}

export const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

export const truncate = (s = "", n = 80) =>
  s.length > n ? s.slice(0, n).trim() + "…" : s;
